/**
 * AdminUnitEconomics (#346) — the unit-economics dashboard.
 *
 * Headline KPI: at the current blended cost per real chat minute,
 * how many real minutes does the 25 CHF/month subscription buy?
 * Answers Bas's viability question: if the answer is < 100 minutes,
 * the price point doesn't work without major optimisation.
 *
 * Three cards:
 *   1. Monthly rollup (LLM / STT / TTS / total + headline KPI)
 *   2. Per-user table (sortable by total cost)
 *   3. Weekly trend (so we can see if optimisations like #344 are
 *      moving the needle)
 *
 * Pricing constants live in `app_config` and are tunable via the
 * pricing-key endpoint. Defaults match Anthropic Sonnet 4.5,
 * Deepgram nova-2, Azure Neural at 2026-05-05 rates.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminGet } from './adminApi';

function fmtMoney(usd) {
  if (usd === null || usd === undefined) return '—';
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}
function fmtChf(chf) {
  if (chf === null || chf === undefined) return '—';
  if (chf < 0.01) return `${chf.toFixed(5)} CHF`;
  if (chf < 1) return `${chf.toFixed(4)} CHF`;
  return `${chf.toFixed(2)} CHF`;
}
function fmtPct(x) {
  if (x === null || x === undefined) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

export default function AdminUnitEconomics() {
  const [summary, setSummary] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [trend, setTrend] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );

  useEffect(() => {
    Promise.all([
      adminGet(`/unit-economics/summary?period=${period}`),
      adminGet(`/unit-economics/by-user?period=${period}&limit=50`),
      adminGet('/unit-economics/trend?weeks=12'),
    ])
      .then(([s, u, t]) => {
        setSummary(s);
        setByUser(u.users || []);
        setTrend(t.series || []);
      })
      .catch(e => setError(e.message));
  }, [period]);

  // Color for the headline KPI: green ≥ 180 min/25CHF, amber 100-180, red < 100.
  const kpiColor = (mins) => {
    if (mins === null || mins === undefined) return '#888';
    if (mins >= 180) return '#27AE60';
    if (mins >= 100) return '#E8845A';
    return '#C0392B';
  };

  return (
    <AdminLayout title="Unit economics">
      <style>{styles}</style>

      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ color: '#666', fontSize: '0.85rem' }}>Period:</label>
        <input
          type="month"
          value={period}
          onChange={e => setPeriod(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #C8C4BD', borderRadius: 6 }}
        />
        <span style={{ color: '#aaa', fontSize: '0.8rem', marginLeft: 'auto' }}>
          LLM costs are authoritative; STT/TTS estimated from real call duration.
          Tune pricing constants via PUT /api/admin/unit-economics/pricing.
        </span>
      </div>

      {error && <div style={{ color: '#C0392B', padding: '12px 0' }}>Error: {error}</div>}

      {/* Card 1 — Monthly rollup + headline KPI */}
      {summary && (
        <div className="card">
          <div className="card-title">{period} — at-a-glance</div>
          <div className="kpi" style={{ color: kpiColor(summary.minutes_per_25chf) }}>
            {summary.minutes_per_25chf !== null
              ? <>≈ <strong>{summary.minutes_per_25chf}</strong> real chat minutes per 25 CHF subscription</>
              : <span style={{ color: '#888', fontWeight: 400, fontSize: '1rem' }}>
                  No real-talk minutes yet this period — make a test call to see numbers.
                </span>}
          </div>
          {summary.minutes_per_25chf !== null && (
            <div className="kpi-sub">
              Target: {summary.target_minutes} min · Current ratio: {Math.round(100 * summary.minutes_per_25chf / summary.target_minutes)}%
            </div>
          )}

          <div className="metrics-grid">
            <div><div className="metric-k">Real talk-time</div><div className="metric-v">{summary.real_minutes} min</div></div>
            <div><div className="metric-k">LLM turns</div><div className="metric-v">{summary.turns}</div></div>
            <div><div className="metric-k">Cache hit rate</div><div className="metric-v">{fmtPct(summary.cache_hit_rate)}</div></div>
            <div><div className="metric-k">Blended $/min</div><div className="metric-v">{fmtChf(summary.blended_per_min_chf)}</div></div>
            <div><div className="metric-k">LLM cost</div><div className="metric-v">{fmtMoney(summary.llm_cost_usd)}</div></div>
            <div><div className="metric-k">STT (est)</div><div className="metric-v">{fmtMoney(summary.stt_cost_usd)}</div></div>
            <div><div className="metric-k">TTS (est)</div><div className="metric-v">{fmtMoney(summary.tts_cost_usd)}</div></div>
            <div><div className="metric-k">Total cost</div><div className="metric-v">{fmtChf(summary.total_cost_chf)}</div></div>
          </div>
        </div>
      )}

      {/* Card 2 — Per-user table */}
      {byUser && (
        <div className="card">
          <div className="card-title">Per-user breakdown ({byUser.length} active)</div>
          {byUser.length === 0 ? (
            <div style={{ color: '#888', padding: '8px 0' }}>No users with token usage in this period.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Family user</th>
                  <th style={{ textAlign: 'right' }}>Turns</th>
                  <th style={{ textAlign: 'right' }}>Real min</th>
                  <th style={{ textAlign: 'right' }}>LLM cost</th>
                  <th style={{ textAlign: 'right' }}>Total CHF</th>
                  <th style={{ textAlign: 'right' }}>$/min</th>
                </tr>
              </thead>
              <tbody>
                {byUser.map(u => (
                  <tr key={u.family_user_id}>
                    <td>
                      <Link to={`/admin/users/${u.family_user_id}`} style={{ color: '#3a8fb7' }}>
                        {u.family_name || u.family_email}
                      </Link>
                      <div style={{ color: '#aaa', fontSize: '0.78rem' }}>{u.family_email}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{u.turns}</td>
                    <td style={{ textAlign: 'right' }}>{u.real_minutes}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(u.llm_cost_usd)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtChf(u.total_cost_chf)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtChf(u.per_min_chf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Card 3 — Weekly trend */}
      {trend && trend.length > 0 && (
        <div className="card">
          <div className="card-title">Weekly trend (last 12 weeks)</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Week starting</th>
                <th style={{ textAlign: 'right' }}>Turns</th>
                <th style={{ textAlign: 'right' }}>Real min</th>
                <th style={{ textAlign: 'right' }}>Total CHF</th>
                <th style={{ textAlign: 'right' }}>$/min CHF</th>
              </tr>
            </thead>
            <tbody>
              {trend.map(w => (
                <tr key={w.week_start}>
                  <td style={{ color: '#888', fontFamily: 'Menlo, monospace', fontSize: '0.8rem' }}>
                    {w.week_start ? w.week_start.slice(0, 10) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{w.turns}</td>
                  <td style={{ textAlign: 'right' }}>{w.real_minutes}</td>
                  <td style={{ textAlign: 'right' }}>{fmtChf(w.total_cost_chf)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtChf(w.per_min_chf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

const styles = `
  .card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 22px 26px;
    margin-bottom: 20px;
  }
  .card-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 14px;
  }
  .kpi {
    font-size: 1.45rem;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .kpi-sub {
    font-size: 0.85rem;
    color: #888;
    margin-bottom: 18px;
  }
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 14px;
    margin-top: 10px;
  }
  .metric-k {
    font-size: 0.78rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .metric-v {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-top: 2px;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table th {
    text-align: left;
    padding: 8px 10px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
  }
  .data-table td {
    padding: 10px;
    font-size: 0.9rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .data-table tr:last-child td { border-bottom: none; }
`;
